import { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';
import Icon from '../components/Icon';
import DifficultyIcons from '../components/DifficultyIcons';
import { Link } from 'react-router-dom';
import BookingModal from '../components/BookingModal';
import QuestionModal from '../components/QuestionModal';
import AutoCarousel from '../components/AutoCarousel';
import FaqPreview from '../components/FaqPreview';
import PriceBanner from '../components/PricePopup';
import { injectIymcsMark } from '../utils/richText';
import { useSeo } from '../hooks/useSeo';
import '../styles/study_page.css';

const safe = (html) => ({ __html: injectIymcsMark(DOMPurify.sanitize(html || '')) });

const Study = ({ isPreview = false }) => {
  const { cards, productionCards } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);

  useSeo({
    title: 'Обучение яхтингу',
    description: 'Курсы яхтинга для начинающих и опытных — теория и практика под руководством сертифицированных инструкторов. Международные сертификаты IYMCS.',
  });

  const source = isPreview ? cards : productionCards;
  const studyCards = (source.study || []).filter(c => c.isPublished === true);
  const cardLink = (card) => isPreview ? `/preview/study/${card.slug || card.id}` : `/study/${card.slug || card.id}`;
  const galleryImages = studyCards.flatMap(c => c.gallery || []).filter(Boolean);
  const hasAnyPrice = studyCards.some(c => c.price);

  return (
    <main className="study-page">
      {hasAnyPrice && <PriceBanner isPreview={isPreview} />}
      <section
        className="study-hero"
        style={content.studyHeroImage ? {
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55),rgba(0,0,0,0.55)), url(${content.studyHeroImage})`
        } : {}}
      >
        <div className="hero-content">
          <h1>Обучение яхтингу</h1>
          <p className="subtitle">
            От первых узлов до управления яхтой в шторм — научим уверенно ходить под парусом
          </p>
        </div>
      </section>
      {!content.studyFeaturesBelow && (content.studyFeaturesTitle || (content.studyFeatures || []).length > 0) && (
        <section className="intro-section">
          <div className="intro-content">
            {content.studyFeaturesTitle && <h2>{content.studyFeaturesTitle}</h2>}
            {content.studyFeaturesText && <div className={content.studyFeaturesTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.studyFeaturesText)} />}
            {(content.studyFeatures || []).length > 0 && (
              <div className="levels-grid">
                {(content.studyFeatures || []).map(f => (
                  <div key={f.id} className="level-card">
                    <Icon name={f.icon} size={28}/>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <section className="courses-list">
        <h2>Программы обучения</h2>
        <div className={`cards-list${studyCards.length === 1 ? ' cards-list--single' : ''}`}>
          {studyCards.map(course => (
            <Link key={course.id} to={cardLink(course)} className="course-card">
              {course.image && (<div className="card-image" style={{ backgroundImage: `url(${course.image})` }}></div>)}
              <div className="card-content">
                <h3>{course.title}</h3>
                <div className="card-meta">
                  {course.duration && (<span><Icon name="clock" size={14}/> {course.duration}</span>)}
                  {course.miles && (<span><Icon name="miles" size={14}/> {course.miles} миль</span>)}
                  {course.level && (<span><Icon name="level" size={14}/> {course.level}</span>)}
                  {course.price && (<span className="card-price-link"><Icon name="money" size={14}/> {course.price}</span>)}
                  {(course.badges || []).map((b, i) => (
                    <span key={`badge-${i}`}><Icon name={b.icon} size={14}/> {b.text}</span>
                  ))}
                  {course.difficulty > 0 && (
                    <DifficultyIcons difficulty={course.difficulty} sectionKey="study" />
                  )}
                </div>
                <div className="card-desc-html" dangerouslySetInnerHTML={safe(course.description)} />
                {course.skills && (
                  <div className="card-highlights">
                    <h4>Вы научитесь:</h4>
                    <ul>
                      {course.skills.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <span className="details-link">Подробнее о курсе →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      {content.studyFeaturesBelow && (content.studyFeaturesTitle || (content.studyFeatures || []).length > 0) && (
        <section className="intro-section">
          <div className="intro-content">
            {content.studyFeaturesTitle && <h2>{content.studyFeaturesTitle}</h2>}
            {content.studyFeaturesText && <div className={content.studyFeaturesTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.studyFeaturesText)} />}
            {(content.studyFeatures || []).length > 0 && (
              <div className="levels-grid">
                {(content.studyFeatures || []).map(f => (
                  <div key={f.id} className="level-card">
                    <Icon name={f.icon} size={28}/>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <FaqPreview faqItems={content.faqItems || []} category="study" faqPath={isPreview ? '/preview/faq' : '/faq'} />
      {galleryImages.length > 0 && (
        <AutoCarousel images={galleryImages} title="Фотогалерея" interval={4500} />
      )}

      <section className="cta-section">
        <h2>{content.studyCtaTitle || 'Остались вопросы?'}</h2>
        {content.studyCtaText && <div className={content.studyCtaTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.studyCtaText)} />}
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>
          Задать вопрос
        </button>
      </section>
      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Запись на курс"
      />
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Обучение" />
    </main>
  );
};

export default Study;
